/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnChanges, type OnInit, type SimpleChanges, Component, EventEmitter, Injector, Input, Output } from '@angular/core';
import { type ColumnDef, type Rest, type TableConfig, ButtonService, TableAbstract, UtilService } from '@dm/core';
import { type Button } from '@dm/core/types';
import { type CIFSClient, type FileSystem } from 'app/common/types/mo';
import { SWITCH } from 'app/config';
import { FileSystemEntryE } from 'app/feature/service/file-service/file-system/create-file-system/create-file-system.component';
import { cloneDeep } from 'lodash';

import { CifsShareStrService } from '../../cifs-share-str.service';
import { AddUserOrGroupComponent } from '../add-user-or-group/add-user-or-group.component';
import { prefixHandler } from '../client-clifs-share';
import { ModifyClientCifsShareComponent } from '../modify-client-cifs-share/modify-client-cifs-share.component';

@Component({
    selector: 'app-front-table-client-cifs-share',
    templateUrl: './front-table-client-cifs-share.component.html',
    styleUrls: ['./front-table-client-cifs-share.component.scss'],
})
export class FrontTableClientCifsShareComponent extends TableAbstract<any> implements OnInit, OnChanges {
    @Input() tableData: CIFSClient[] = [];
    @Input() selectFs: FileSystem;
    @Input() vstoreId: string;
    @Input() entry: FileSystemEntryE;
    @Input() vstoreName: string;
    @Input() supportTableEdit = false;
    /**
     * 三步创共享中
     */
    @Input() isCopyFrom = false;
    // tableData的双向绑定实现，每一次改变值，都要emit出去
    @Output() tableDataChange: EventEmitter<any> = new EventEmitter();
    // 点击了添加按钮就emit
    @Output() clickAddBtn?: EventEmitter<any> = new EventEmitter();
    @Input() supportSmartGui = true;
    i18n = this.str.i18n;

    tableRest: Rest;
    tableCountRest: Rest;
    tableConfig: TableConfig;
    tableColumnDefs: ColumnDef[];
    selection: any[] = [];

    saveTableData: CIFSClient[] = [];

    addBtn: Button;
    modifyBtn: Button;
    removeBtn: Button;

    SWITCH = SWITCH;
    Number = Number;
    FileSystemEntryE = FileSystemEntryE;

    constructor(
        public str: CifsShareStrService,
        private buttonService: ButtonService,
        private utilService: UtilService,
        injector: Injector,
    ) {
        super(injector);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.tableData) {
            this.saveTableData = cloneDeep(this.tableData);
        }
    }

    initBtns(): void {
        this.addBtn = this.buttonService.create({
            id: 'add-client-cifs-share',
            label: this.i18n.btn.addBtn,
            theme: 'primary',
            depPermission: ['task_W'],
            command: () => {
                this.add();
            },
        });
        this.modifyBtn = this.buttonService.create({
            id: 'modify-client-cifs-share',
            label: this.i18n.btn.modifyBtn,
            command: ({ data }) => {
                this.modify(data);
            },
        });
        this.removeBtn = this.buttonService.create({
            id: 'remove-client-cifs-share',
            label: this.i18n.btn.removeBtn,
            disabledCheck: (data) => !data && this.selection.length === 0,
            command: ({ data }) => {
                this.remove(data ? [data] : this.selection);
            },
        });
    }

    initTable(): void {
        this.tableConfig = {
            dataKey: 'ID',
            selectionMode: 'checkbox',
            menuItems: this.supportTableEdit ? [] : [
                this.modifyBtn,
                this.removeBtn,
            ],
            beforeShow: (data: CIFSClient[]): CIFSClient[] => this.addId(data),
        };

        this.tableColumnDefs = [
            {
                field: 'NAME',
                header: this.i18n.label.userOrGroup,
                filterType: 'search',
                render: (value: string) => prefixHandler(value), // 如果是组，会多一个@符号
                filterTrim: false,
            },
            {
                field: 'DOMAINTYPE',
                header: this.i18n.label.type,
                columnFilter: true,
                render: (value, col, data: CIFSClient): string => this.str.getUpperCaseUserOrGroupType(data),
            },
            {
                field: 'PERMISSION',
                header: this.i18n.label.permissionLevel,
                columnFilter: true,
                filterType: 'dropdown',
                options: this.str.permissionLevelOpts,
                render: (value) => this.utilService.getLabelFromOptions(this.str.permissionLevelOpts, Number(value)),
            },
            {
                header: this.i18n.label.option,
                field: 'buttons',
                ignore: !this.supportTableEdit,
                width: 120,
            },
        ];
    }

    /**
     * 使用domaintype和name拼接一个虚拟ID
     */
    addId(data: CIFSClient[]): CIFSClient[] {
        data.forEach((item) => {
            item.ID = item.DOMAINTYPE + item.NAME;

            // 通过接口获取的DOMAINTYPE是字符串，“模板创建”直接使用会报错，因此转成数字
            item.DOMAINTYPE = Number(item.DOMAINTYPE);

            if (!Object.hasOwnProperty.call(item, 'editStatus')) {
                item.editStatus = false;
            }
        });
        return data;
    }

    updateTableData(): void {
        this.tableDataChange.emit(this.tableData);
        this.dt.updateData(this.tableData);
    }

    add(): void {
        if (this.clickAddBtn) {
            this.clickAddBtn.emit();
        }

        // 没有租户信息无法添加权限
        if (!this.vstoreId && !this.selectFs) {
            return;
        }
        this.doAction(AddUserOrGroupComponent, {
            isFrontTable: true,
            supportSmartGui: this.supportSmartGui,
            vstoreId: this.vstoreId || this.selectFs.vstoreId,
            selectedVstore: {
                label: this.vstoreName || this.selectFs.vstoreName,
                value: this.vstoreId || this.selectFs.vstoreId,
            },
            clients: this.tableData,
            entry: this.entry,
            fileSystem: this.selectFs,
            callBack: (datas: CIFSClient[]) => {
                datas.forEach((value) => {
                    // 先校验重复，再添加，防止誤操作
                    const tempIndex = this.tableData.findIndex((item) => item.ID === value.ID);
                    if (tempIndex === -1) {
                        this.tableData.push(value);
                    }
                });
                // 用...触发angualr刷新，push触发不了
                this.tableData = [...this.tableData];

                this.saveTableData = cloneDeep(this.tableData);
                this.updateTableData();
            },

        });
    }

    modify(data: CIFSClient): void {
        this.doAction(ModifyClientCifsShareComponent, {
            data,
            isFrontTable: true,
            clients: this.tableData,
            entry: this.entry,
            fileSystem: this.selectFs,
            callBack: (value: CIFSClient) => {
                // 找到老位置
                const oldIndex = this.tableData.findIndex((item) => item.ID === data.ID);
                // 把老位置内容换了
                if (oldIndex !== -1) {
                    this.tableData[oldIndex] = { ...this.tableData[oldIndex], ...value };
                    this.updateTableData();
                }
            },
        });
    }

    /**
     * 前端删除
     * @param removeList 待删除列表
     */
    remove(removeList: CIFSClient[]): void {
        this.tableData = this.tableData.filter((data) => {
            // 如果这个data的名字 在removeList中，那就得删掉
            const tempIndex = removeList.findIndex((removeItem) => removeItem.ID === data.ID);
            // filter true的留下， false的删掉
            return tempIndex === -1;
        });

        // 更改数据后，刷新表格，触发双向绑定
        this.updateTableData();
    }

    /**
     * 确定
     */
    confirmClient(item: CIFSClient): void {
        item.editStatus = false;

        this.saveTableData = cloneDeep(this.tableData);
    }

    /**
     * 修改
     */
    modifyClient(item: CIFSClient): void {
        item.editStatus = true;
    }

    /**
     * 取消
     */
    cancelModifyClient(item: CIFSClient): void {
        const clientItem = this.saveTableData.find((value) => value.NAME === item.NAME);
        if (!clientItem) {
            this.deleteClient(item);
        } else {
            const colneItem = cloneDeep(clientItem);
            Object.assign(item, colneItem);
            item.editStatus = false;
        }
    }

    /**
     * 删除
     */
    deleteClient(item: CIFSClient): void {
        this.remove([item]);
    }
}
